import React from "react";
import { Box } from "@mui/material";
import Moment from "react-moment";
import { dt } from "../utils/dt";

interface PersonBylineProps {
  variant: "creator" | "solver";
  label: string;
  name?: string;
  role?: string;
  date?: Date;
  isPhone?: boolean;
}

const PersonByline: React.FC<PersonBylineProps> = ({
  variant,
  label,
  name,
  role,
  date,
  isPhone = false,
}) => {
  if (!name) return null;

  const isCreator = variant === "creator";
  const tokens = dt.serviceReports.byline;
  const circleBg = isCreator ? tokens.creatorCircleBg : tokens.solverCircleBg;
  const circleText = isCreator ? tokens.creatorCircleText : tokens.solverCircleText;
  const borderColor = isCreator ? tokens.creatorBorder : tokens.solverBorder;

  const initials = name
    .split(" ")
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase()
    .substring(0, 2);

  return (
    <Box>
      {/* Section label */}
      <Box
        sx={{
          fontFamily: dt.font,
          fontSize: "11px",
          fontWeight: 600,
          color: tokens.labelColor,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          mb: 0.75,
        }}
      >
        {label}
      </Box>

      {/* Card */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "14px 16px",
          borderRadius: dt.radiusSm,
          borderLeft: `3px solid ${borderColor}`,
          backgroundColor: "#FAFBFC",
        }}
      >
        {/* Initial circle */}
        <Box
          sx={{
            width: isPhone ? "32px" : "36px",
            height: isPhone ? "32px" : "36px",
            borderRadius: "50%",
            backgroundColor: circleBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              fontWeight: 700,
              color: circleText,
            }}
          >
            {initials}
          </Box>
        </Box>

        {/* Text content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "14px",
                fontWeight: 600,
                color: dt.textPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {name}
            </Box>
            {role && (
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "11px",
                  fontWeight: 500,
                  color: tokens.roleBadgeText,
                  backgroundColor: tokens.roleBadgeBg,
                  padding: "2px 8px",
                  borderRadius: "10px",
                }}
              >
                {role}
              </Box>
            )}
          </Box>
          {date && (
            <Box
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "12px",
                color: dt.textMuted,
                mt: 0.25,
              }}
            >
              <Moment date={date} format="DD.MM.YYYY, HH:mm" />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PersonByline;
